import React, { useEffect } from 'react'
import { Form, Input, Button, Typography } from 'antd'
import { Mail, Lock, Building, User } from 'lucide-react'
import type { AuthFormData } from '../../types/auth'
import { useCompanyStore } from '../../store/company/companyStore'

const { Text } = Typography

interface Props {
  type: 'login' | 'signup'
  onSubmit: (data: AuthFormData) => void
  loading?: boolean
  error?: string
}

export const AuthForm: React.FC<Props> = ({
  type,
  onSubmit,
  loading,
  error
}) => {
  const { company, patchCompany } = useCompanyStore((store) => store)

  const initialValues = type === 'signup' ? { company: company.name } : {}
  const [form] = Form.useForm<AuthFormData>()

  useEffect(() => {
    form.setFieldValue('company', company.name)
  }, [company])

  const companyName = Form.useWatch('company', form)

  useEffect(() => {
    if (companyName) {
      patchCompany({ name: companyName })
    }
  }, [companyName])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      layout="vertical"
      onFinish={onSubmit}
    >
      {error && (
        <Text type="danger" className="block mb-4">
          {error}
        </Text>
      )}

      {type === 'signup' && (
        <>
          <Form.Item
            name="name"
            rules={[
              {
                required: true,
                message: 'Please enter your name'
              }
            ]}
          >
            <Input
              prefix={<User className="h-4 w-4 text-gray-400" />}
              placeholder="Full Name"
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="company"
            rules={[
              { required: true, message: 'Please enter your company name' }
            ]}
          >
            <Input
              prefix={<Building className="h-4 w-4 text-gray-400" />}
              placeholder="Company Name"
              size="large"
            />
          </Form.Item>
        </>
      )}

      <Form.Item
        name="email"
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Please enter a valid email' }
        ]}
      >
        <Input
          prefix={<Mail className="h-4 w-4 text-gray-400" />}
          placeholder="Email Address"
          size="large"
        />
      </Form.Item>

      <Form.Item
        name="password"
        rules={[
          { required: true, message: 'Please enter your password' },
          { min: 8, message: 'Password must be at least 8 characters' }
        ]}
      >
        <Input.Password
          prefix={<Lock className="h-4 w-4 text-gray-400" />}
          placeholder="Password"
          size="large"
        />
      </Form.Item>

      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          block
          loading={loading}
        >
          {type === 'login' ? 'Sign In' : 'Create Free Account'}
        </Button>
      </Form.Item>
    </Form>
  )
}
